"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import type { Message } from "@/lib/core/crm/types/message";
import {
  useMessageRelatedEntities,
  type RelatedDeal,
  type RelatedTask,
  type RelatedTicket,
} from "@/lib/hooks/messages/use-message-related-entities";
import { buildModalUrl } from "@/lib/modals";
import { Badge, EmptyState, Skeleton } from "@wtc/ui";
import { Link2, ListTodo, TrendingUp, Ticket } from "lucide-react";
import { cn } from "@/lib/utils";

interface MessageRelatedTabProps {
  message: Message;
  className?: string;
}

const stageBadgeClass =
  "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300";
const statusBadgeClass =
  "bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300";
const priorityBadgeClass =
  "bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300";

function humanize(value: string | null): string {
  if (!value) return "";
  return value.replace(/_/g, " ");
}

interface RelatedRowProps {
  title: string;
  badges: Array<{ key: string; label: string; className: string }>;
  onOpen: () => void;
}

function RelatedRow({ title, badges, onOpen }: RelatedRowProps) {
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onOpen();
      }
    },
    [onOpen],
  );

  return (
    <div
      className="flex items-start justify-between gap-2 p-2 border rounded-md bg-card cursor-pointer transition-all hover:shadow-sm hover:border-primary/50"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <span className="text-xs font-medium truncate flex-1 min-w-0">{title}</span>
      <div className="flex items-center gap-1 flex-shrink-0">
        {badges.map((b) =>
          b.label ? (
            <Badge key={b.key} className={cn("text-[10px]", b.className)}>
              {b.label}
            </Badge>
          ) : null,
        )}
      </div>
    </div>
  );
}

interface RelatedSectionProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  count: number;
  children: React.ReactNode;
}

function RelatedSection({ icon: Icon, label, count, children }: RelatedSectionProps) {
  if (count === 0) return null;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground px-1">
        <Icon className="h-3.5 w-3.5" />
        {label}
        <span className="text-muted-foreground/70">({count})</span>
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

export function MessageRelatedTab({ message, className }: MessageRelatedTabProps) {
  const router = useRouter();
  const { data, isLoading, isResolvingContacts, hasResolvedContacts } =
    useMessageRelatedEntities(message);

  const openDeal = React.useCallback(
    (deal: RelatedDeal) => {
      router.push(buildModalUrl("deals-detail", { dealId: deal.id }, { pathname: "/deals" }));
    },
    [router],
  );

  const openTicket = React.useCallback(
    (ticket: RelatedTicket) => {
      router.push(
        buildModalUrl("tickets-detail", { ticketId: ticket.id }, { pathname: "/tickets" }),
      );
    },
    [router],
  );

  const openTask = React.useCallback(
    (task: RelatedTask) => {
      router.push(buildModalUrl("tasks-detail", { taskId: task.id }, { pathname: "/tasks" }));
    },
    [router],
  );

  const totalCount = data.deals.length + data.tickets.length + data.tasks.length;
  const loading = isResolvingContacts || isLoading;

  if (loading) {
    return (
      <div className={cn("space-y-3", className)}>
        <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground px-1">
          <Link2 className="h-4 w-4" />
          Related to participants
        </div>
        <div className="space-y-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground px-1">
        <Link2 className="h-4 w-4" />
        Related to participants
      </div>

      {totalCount === 0 ? (
        <EmptyState
          icon={Link2}
          size="sm"
          title="No related items"
          description={
            hasResolvedContacts
              ? "No active deals, open tickets, or open tasks for the people on this thread."
              : "None of the people on this thread match a contact in the CRM yet."
          }
        />
      ) : (
        <div className="space-y-4">
          <RelatedSection icon={TrendingUp} label="Active Deals" count={data.deals.length}>
            {data.deals.map((deal) => (
              <RelatedRow
                key={deal.id}
                title={deal.title || "Untitled deal"}
                badges={[
                  { key: "stage", label: humanize(deal.stage), className: stageBadgeClass },
                ]}
                onOpen={() => openDeal(deal)}
              />
            ))}
          </RelatedSection>

          <RelatedSection icon={Ticket} label="Open Tickets" count={data.tickets.length}>
            {data.tickets.map((ticket) => (
              <RelatedRow
                key={ticket.id}
                title={ticket.title || "Untitled ticket"}
                badges={[
                  { key: "status", label: humanize(ticket.status), className: statusBadgeClass },
                  {
                    key: "priority",
                    label: humanize(ticket.priority),
                    className: priorityBadgeClass,
                  },
                ]}
                onOpen={() => openTicket(ticket)}
              />
            ))}
          </RelatedSection>

          <RelatedSection icon={ListTodo} label="Open Tasks" count={data.tasks.length}>
            {data.tasks.map((task) => (
              <RelatedRow
                key={task.id}
                title={task.title || "Untitled task"}
                badges={[
                  { key: "status", label: humanize(task.status), className: statusBadgeClass },
                  {
                    key: "priority",
                    label: humanize(task.priority),
                    className: priorityBadgeClass,
                  },
                ]}
                onOpen={() => openTask(task)}
              />
            ))}
          </RelatedSection>
        </div>
      )}
    </div>
  );
}
